import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";

import { functions } from "../../Security/firebase-config";
import { useTeam } from "../../context/TeamContext";
import { FUNCTION_NAMES } from "../../utils/functionNames";
import { buildTeamScopedPath, getRequestedTeamId } from "../../utils/teamRouteTarget";
import DeleteHighlightConfirm from "../../components/core-components/DeleteHighlightConfirm";
import CustomAlert from "../../components/core-components/CustomAlert";
import Footer from "../../components/core-components/Footer";

import "./Billing.css";

function getRequiredFunctionName(key) {
  const functionName = FUNCTION_NAMES?.[key];
  if (typeof functionName === "string" && functionName.trim()) {
    return functionName.trim();
  }

  throw new Error(`Missing Firebase function mapping for "${key}".`);
}

function formatMoneyFromMinor(amountMinor, currency) {
  if (typeof amountMinor !== "number" || !Number.isFinite(amountMinor)) {
    return "-";
  }

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency || "CAD",
    currencyDisplay: "code",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountMinor / 100);
}

function safeToMillis(value) {
  if (!value) return null;

  if (typeof value?.toDate === "function") {
    const dateValue = value.toDate();
    return Number.isNaN(dateValue?.getTime?.()) ? null : dateValue.getTime();
  }

  if (typeof value?._seconds === "number") {
    return value._seconds * 1000;
  }

  if (typeof value?.seconds === "number") {
    return value.seconds * 1000;
  }

  if (typeof value === "number") {
    return value > 10_000_000_000 ? value : value * 1000;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.getTime();
  }

  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
  }

  return null;
}

function formatBillingDeadline(value, teamTimeZone) {
  const ms = safeToMillis(value);

  if (!ms) {
    return "Not scheduled yet";
  }

  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  };

  if (typeof teamTimeZone === "string" && teamTimeZone.trim()) {
    options.timeZone = teamTimeZone.trim();
  }

  try {
    return new Intl.DateTimeFormat("en-CA", options).format(new Date(ms));
  } catch (_) {
    return new Date(ms).toLocaleString(undefined, options);
  }
}

function formatLocalBillingDeadline(value) {
  return formatBillingDeadline(value, null);
}

function formatHistoryWindow(startValue, endValue) {
  const startMs = safeToMillis(startValue);
  const endMs = safeToMillis(endValue);

  if (startMs && endMs) {
    return `${formatLocalBillingDeadline(startValue)} to ${formatLocalBillingDeadline(endValue)}`;
  }

  if (endMs) {
    return `Ended ${formatLocalBillingDeadline(endValue)}`;
  }

  if (startMs) {
    return `Started ${formatLocalBillingDeadline(startValue)}`;
  }

  return "Cycle window unavailable";
}

function formatHistorySourceLabel(source) {
  if (typeof source !== "string" || !source.trim()) {
    return "History";
  }

  return source
    .trim()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getHistoryStatementMillis(entry) {
  return (
    safeToMillis(entry?.currentPeriodEnd) ||
    safeToMillis(entry?.currentPeriodStart) ||
    safeToMillis(entry?.createdAt) ||
    safeToMillis(entry?.updatedAt) ||
    null
  );
}

function getHistoryStatementMonthKey(entry) {
  const statementMs = getHistoryStatementMillis(entry);

  if (!statementMs) {
    return "undated";
  }

  const statementDate = new Date(statementMs);
  return `${statementDate.getFullYear()}-${String(statementDate.getMonth() + 1).padStart(
    2,
    "0"
  )}`;
}

function formatHistoryMonthLabel(monthKey) {
  if (monthKey === "undated") {
    return "Undated records";
  }

  const [yearValue, monthValue] = String(monthKey)
    .split("-")
    .map((part) => Number(part));

  if (!Number.isFinite(yearValue) || !Number.isFinite(monthValue)) {
    return "Statement records";
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "long",
    year: "numeric",
  }).format(new Date(yearValue, monthValue - 1, 1));
}

function formatStatusLabel(status) {
  if (typeof status !== "string" || !status.trim()) {
    return "Inactive";
  }

  return status
    .trim()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatPlanLabel(planKey) {
  if (typeof planKey !== "string" || !planKey.trim()) {
    return "No subscription";
  }

  return planKey.charAt(0).toUpperCase() + planKey.slice(1);
}

function formatCycleUnit(interval) {
  const normalized = typeof interval === "string" ? interval.trim().toLowerCase() : "";

  if (normalized === "monthly") {
    return "month";
  }

  if (normalized === "yearly" || normalized === "annual") {
    return "year";
  }

  return "cycle";
}

function normalizePendingChangeTiming(value) {
  return value === "immediate_prorated" ? "immediate_prorated" : "next_cycle";
}

function isImmediatePendingChange(row) {
  return (
    row?.pendingNextCycleChange === true &&
    normalizePendingChangeTiming(row?.pendingChangeTiming) === "immediate_prorated"
  );
}

function resolveUpcomingBillingBoundary(row) {
  const nowMs = Date.now();
  const trialBoundaryMs = safeToMillis(
    row?.trialEndsAt || row?.currentCycleSummary?.trialEndsAt
  );
  const currentBoundaryMs = safeToMillis(
    row?.currentPeriodEnd || row?.currentCycleSummary?.currentPeriodEnd
  );
  const renewalBoundaryMs = safeToMillis(
    row?.nextRenewalAt || row?.currentCycleSummary?.nextRenewalAt
  );
  const normalizedStatus =
    typeof row?.status === "string" ? row.status.trim().toLowerCase() : "";

  if (normalizedStatus === "trialing" && trialBoundaryMs && trialBoundaryMs > nowMs) {
    return {
      value: row?.trialEndsAt || row?.currentCycleSummary?.trialEndsAt || null,
      isTrialBoundary: true,
    };
  }

  if (currentBoundaryMs) {
    return {
      value: row?.currentPeriodEnd || row?.currentCycleSummary?.currentPeriodEnd || null,
      isTrialBoundary: false,
    };
  }

  if (renewalBoundaryMs && renewalBoundaryMs > nowMs) {
    return {
      value: row?.nextRenewalAt || row?.currentCycleSummary?.nextRenewalAt || null,
      isTrialBoundary: false,
    };
  }

  if (trialBoundaryMs && trialBoundaryMs > nowMs) {
    return {
      value: row?.trialEndsAt || row?.currentCycleSummary?.trialEndsAt || null,
      isTrialBoundary: true,
    };
  }

  return {
    value: row?.pendingNextCycleSummary?.deadlineAt || null,
    isTrialBoundary: false,
  };
}

function getAlertRequirementCopy(alert) {
  const requirements = [];

  if (alert?.needsApproval) {
    requirements.push("approve the split");
  }

  if (alert?.needsPaymentMethod) {
    requirements.push("add a card");
  }

  if (!requirements.length) {
    return "review the payer setup";
  }

  if (requirements.length === 1) {
    return requirements[0];
  }

  return `${requirements[0]} and ${requirements[1]}`;
}

function formatPayerNames(profiles) {
  if (!Array.isArray(profiles) || profiles.length === 0) {
    return "No payers selected";
  }

  return profiles
    .map((profile) => profile?.displayName || profile?.uid || "Unknown payer")
    .join(", ");
}

function getStatusTone(row) {
  const normalizedStatus =
    typeof row?.status === "string" ? row.status.trim().toLowerCase() : "";

  if (row?.pendingNextCycleChange) {
    if (
      row?.pendingNextCycleSummary?.allApproved !== true ||
      row?.pendingNextCycleSummary?.allPaymentMethodsReady !== true
    ) {
      return "warning";
    }

    return "muted";
  }

  if (normalizedStatus === "active" || normalizedStatus === "trialing") {
    return "success";
  }

  if (
    normalizedStatus === "past_due" ||
    normalizedStatus === "billing_hold" ||
    normalizedStatus === "renewal_blocked"
  ) {
    return "warning";
  }

  if (row?.cancelAtPeriodEnd) {
    return "warning";
  }

  return "muted";
}

function getStatusBadgeLabel(row) {
  if (row?.pendingNextCycleChange) {
    return isImmediatePendingChange(row) ? "Pending upgrade" : "Queued change";
  }

  if (row?.cancelAtPeriodEnd) {
    return "Renewal off";
  }

  return formatStatusLabel(row?.status);
}

function getPrimaryActionCopy(row) {
  const setupStatus =
    typeof row?.setupStatus === "string" ? row.setupStatus.trim().toLowerCase() : "";

  if (
    setupStatus === "pending_payer_approvals" ||
    setupStatus === "pending_next_cycle_payer_approvals"
  ) {
    return "Waiting on payer approvals";
  }

  if (
    setupStatus === "pending_payment_methods" ||
    setupStatus === "pending_next_cycle_payment_methods"
  ) {
    return "Waiting on payer cards";
  }

  if (row?.pendingNextCycleChange && row?.pendingNextCycleSummary?.allApproved !== true) {
    return isImmediatePendingChange(row)
      ? "Upgrade needs approvals"
      : "Next cycle change needs approvals";
  }

  if (
    row?.pendingNextCycleChange &&
    row?.pendingNextCycleSummary?.allPaymentMethodsReady !== true
  ) {
    return isImmediatePendingChange(row)
      ? "Upgrade needs cards"
      : "Next cycle change needs cards";
  }

  if (row?.pendingNextCycleChange) {
    return isImmediatePendingChange(row)
      ? "Upgrade activates with proration"
      : "Next cycle change is queued";
  }

  if (row?.cancelAtPeriodEnd) {
    return "Renewal ends after this cycle";
  }

  return "Billing looks healthy";
}

function formatPaymentMethodSummary(paymentMethod) {
  if (!paymentMethod) {
    return "No saved card on file";
  }

  const brand = paymentMethod.brand ? paymentMethod.brand.toUpperCase() : "CARD";
  const ending = paymentMethod.last4 || "****";
  const expMonth = paymentMethod.expMonth
    ? String(paymentMethod.expMonth).padStart(2, "0")
    : "??";
  const expYear = paymentMethod.expYear || "????";

  return `${brand} ending in ${ending} | expires ${expMonth}/${expYear}`;
}

function buildDiscountSummary(row) {
  const totalDiscountMinor =
    typeof row?.totalPromoDiscountAmountMinor === "number"
      ? row.totalPromoDiscountAmountMinor
      : 0;

  if (totalDiscountMinor <= 0) {
    return {
      primary: "No recurring discounts",
      secondary: null,
    };
  }

  const detailParts = [];

  if (typeof row?.teamBillPromoDiscountAmountMinor === "number" && row.teamBillPromoDiscountAmountMinor > 0) {
    detailParts.push(
      `${formatMoneyFromMinor(row.teamBillPromoDiscountAmountMinor, row.billingCurrency)} team`
    );
  }

  if (typeof row?.userBillPromoDiscountAmountMinor === "number" && row.userBillPromoDiscountAmountMinor > 0) {
    detailParts.push(
      `${formatMoneyFromMinor(row.userBillPromoDiscountAmountMinor, row.billingCurrency)} payer`
    );
  }

  return {
    primary: formatMoneyFromMinor(totalDiscountMinor, row?.billingCurrency),
    secondary: detailParts.length ? detailParts.join(" + ") : "Recurring discount applied",
  };
}

const Billing = ({ embedded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { teamId: contextTeamId } = useTeam();
  const requestedTeamId = getRequestedTeamId(location.search);

  const [loading, setLoading] = useState(true);
  const [billingOverview, setBillingOverview] = useState(null);
  const [teamSetupMeta, setTeamSetupMeta] = useState({});
  const [expandedTeamRows, setExpandedTeamRows] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [billingHistory, setBillingHistory] = useState([]);
  const [selectedHistoryMonth, setSelectedHistoryMonth] = useState("");
  const [historyError, setHistoryError] = useState("");
  const [pageError, setPageError] = useState("");
  const [toast, setToast] = useState({ message: "", type: "" });
  const [confirmAction, setConfirmAction] = useState(null);

  const showAlert = useCallback((message, type = "info") => {
    setToast({ message, type });
    window.setTimeout(() => {
      setToast((current) => (current.message === message ? { message: "", type: "" } : current));
    }, 5000);
  }, []);

  const loadBillingData = useCallback(async () => {
    try {
      setLoading(true);
      setPageError("");

      const getOverview = httpsCallable(
        functions,
        getRequiredFunctionName("getMyBillingOverview")
      );
      const getAlerts = FUNCTION_NAMES.getMyPayerActionAlerts
        ? httpsCallable(
            functions,
            getRequiredFunctionName("getMyPayerActionAlerts")
          )
        : null;

      const [overviewResult, alertResult] = await Promise.all([
        getOverview({}),
        getAlerts ? getAlerts({}) : Promise.resolve({ data: { alerts: [] } }),
      ]);

      const overviewData = overviewResult?.data || null;
      const alertData = alertResult?.data || {};
      const overviewTeams = Array.isArray(overviewData?.teams) ? overviewData.teams : [];
      const nextTeamSetupMeta = {};

      if (FUNCTION_NAMES.getTeamBillingSetup && overviewTeams.length) {
        try {
          const getTeamBillingSetup = httpsCallable(
            functions,
            FUNCTION_NAMES.getTeamBillingSetup
          );

          const teamSetupResults = await Promise.allSettled(
            overviewTeams.map((row) => getTeamBillingSetup({ teamId: row.teamId }))
          );

          teamSetupResults.forEach((result, index) => {
            const row = overviewTeams[index];

            if (!row || result.status !== "fulfilled") {
              return;
            }

            const teamSetupData = result.value?.data || {};
            nextTeamSetupMeta[row.teamId] = {
              billableSeatCount:
                typeof teamSetupData?.billingSummary?.billableSeatCount === "number"
                  ? teamSetupData.billingSummary.billableSeatCount
                  : null,
            };
          });
        } catch (teamSetupError) {
          console.warn("Billing supplemental metadata failed:", teamSetupError);
        }
      }

      setBillingOverview(overviewData);
      setTeamSetupMeta(nextTeamSetupMeta);
      setAlerts(Array.isArray(alertData?.alerts) ? alertData.alerts : []);
    } catch (error) {
      console.error("loadBillingData error:", error);
      setPageError(error?.message || "Failed to load billing overview.");
      setBillingOverview(null);
      setTeamSetupMeta({});
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBillingHistory = useCallback(async () => {
    if (!FUNCTION_NAMES.getMyBillingHistory) {
      setHistoryError("Billing history is not available in this build yet.");
      setBillingHistory([]);
      return;
    }

    try {
      setHistoryLoading(true);
      setHistoryError("");

      const getHistory = httpsCallable(
        functions,
        getRequiredFunctionName("getMyBillingHistory")
      );
      const historyResult = await getHistory({});
      const historyData = historyResult?.data || {};
      setBillingHistory(Array.isArray(historyData?.history) ? historyData.history : []);
    } catch (error) {
      console.error("loadBillingHistory error:", error);
      setHistoryError(error?.message || "Failed to load billing history.");
      setBillingHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBillingData();
  }, [loadBillingData]);

  const billingTeams = useMemo(
    () => (Array.isArray(billingOverview?.teams) ? billingOverview.teams : []),
    [billingOverview]
  );

  const activeTeamId = useMemo(() => {
    if (requestedTeamId && billingTeams.some((row) => row.teamId === requestedTeamId)) {
      return requestedTeamId;
    }

    if (contextTeamId && billingTeams.some((row) => row.teamId === contextTeamId)) {
      return contextTeamId;
    }

    return billingTeams[0]?.teamId || null;
  }, [billingTeams, contextTeamId, requestedTeamId]);

  const activeTeamBillingRow = useMemo(() => {
    if (!activeTeamId) return null;
    return billingTeams.find((row) => row.teamId === activeTeamId) || null;
  }, [activeTeamId, billingTeams]);

  const activeTeamAlerts = useMemo(() => {
    if (!activeTeamId) return [];
    return alerts.filter((alert) => alert.teamId === activeTeamId);
  }, [activeTeamId, alerts]);

  const currentPayerRoleCount = useMemo(
    () => billingTeams.filter((row) => row.isCurrentPayer).length,
    [billingTeams]
  );

  const renewalEndingCount = useMemo(
    () => billingTeams.filter((row) => row.cancelAtPeriodEnd).length,
    [billingTeams]
  );

  const payerManagedTeams = useMemo(
    () =>
      billingTeams.filter(
        (row) => row.isCurrentPayer || row.isNextCyclePayer || !!row.paymentMethod
      ),
    [billingTeams]
  );

  const primaryPaymentRow = useMemo(() => {
    if (
      activeTeamBillingRow &&
      (
        activeTeamBillingRow.isCurrentPayer ||
        activeTeamBillingRow.isNextCyclePayer ||
        !!activeTeamBillingRow.paymentMethod
      )
    ) {
      return activeTeamBillingRow;
    }

    return payerManagedTeams[0] || null;
  }, [activeTeamBillingRow, payerManagedTeams]);

  const savedCardCount = useMemo(
    () => billingTeams.filter((row) => !!row.paymentMethod).length,
    [billingTeams]
  );

  const focusedActionAlert = activeTeamAlerts[0] || alerts[0] || null;

  const billingStatusStrip = useMemo(() => {
    if (loading) {
      return {
        tone: "muted",
        title: "Loading billing center",
        copy: "Refreshing your billing rows, cards, approvals, and renewal boundaries.",
        actionTeamId: null,
      };
    }

    if (focusedActionAlert) {
      return {
        tone: "warning",
        title: `${focusedActionAlert.teamName || "A team"} needs you to ${getAlertRequirementCopy(
          focusedActionAlert
        )}`,
        copy:
          focusedActionAlert.alertType === "next_cycle_transition_blocker"
            ? `Resolve this before ${formatLocalBillingDeadline(
                focusedActionAlert.deadlineAt
              )} so the queued billing cycle can transition cleanly.`
            : "Resolve this before the affected team subscription can start or continue smoothly.",
        actionTeamId: focusedActionAlert.teamId || null,
      };
    }

    if (!billingTeams.length) {
      return {
        tone: "muted",
        title: "No team billing rows are attached to this account yet",
        copy: "Once this account becomes a selected payer or joins a subscribed team, the breakdown will appear here.",
        actionTeamId: null,
      };
    }

    if (renewalEndingCount > 0) {
      return {
        tone: "muted",
        title: `${renewalEndingCount} team billing cycle${
          renewalEndingCount === 1 ? "" : "s"
        } currently end after the present cycle`,
        copy: "Those teams stay active until their current end date, but they are not scheduled to renew automatically afterward.",
        actionTeamId: null,
      };
    }

    return {
      tone: "success",
      title: "No billing issues need resolving right now",
      copy: "Your current payer approvals, cards, and renewals look healthy across this billing account.",
      actionTeamId: null,
    };
  }, [billingTeams.length, focusedActionAlert, loading, renewalEndingCount]);

  const billingBreakdownRows = useMemo(
    () =>
      billingTeams.map((row) => {
        const roleCopy = row.isCurrentPayer
          ? "Current payer"
          : row.isNextCyclePayer
            ? "Queued next-cycle payer"
            : "Not a payer";
        const teamCycleSubtotalMinor =
          typeof row.teamComputedTotalMinor === "number"
            ? row.teamComputedTotalMinor
            : typeof row.teamTotalMinor === "number"
              ? row.teamTotalMinor
              : null;
        const grossTeamCycleMinor =
          typeof teamCycleSubtotalMinor === "number"
            ? teamCycleSubtotalMinor + (row.teamBillPromoDiscountAmountMinor || 0)
            : null;
        const discountSummary = buildDiscountSummary(row);
        const billingBoundary = resolveUpcomingBillingBoundary(row);
        const nextPaymentValue = billingBoundary.value;
        const isTrialBoundary = billingBoundary.isTrialBoundary;
        const nextPaymentPrimary = nextPaymentValue
          ? formatLocalBillingDeadline(nextPaymentValue)
          : row.pendingNextCycleChange
            ? "Waiting for Stripe sync"
            : row.isCurrentPayer || row.isNextCyclePayer
              ? "Setup or sync pending"
              : "Not active yet";
        const nextPaymentLabel =
          !nextPaymentValue
            ? row.pendingNextCycleChange
              ? "Renewal date will appear once the current cycle sync finishes."
              : row.isCurrentPayer || row.isNextCyclePayer
                ? "The next renewal boundary will appear after billing setup and Stripe sync complete."
                : "This team does not have an active renewal date yet."
            : isTrialBoundary
              ? "Free trial end"
              : isImmediatePendingChange(row)
                ? "Current renewal date while the approved upgrade waits to activate with proration"
                : row.pendingNextCycleChange
                  ? "Current cycle end before queued change"
                  : row.cancelAtPeriodEnd
                    ? "Subscription closes after this cycle"
                    : `Current billing ${formatCycleUnit(row.billingInterval)} end`;
        const sharePrimary =
          row.isCurrentPayer && typeof row.currentUserTotalMinor === "number"
            ? formatMoneyFromMinor(row.currentUserTotalMinor, row.billingCurrency)
            : row.isNextCyclePayer && typeof row.currentUserShareMinor === "number"
              ? formatMoneyFromMinor(row.currentUserShareMinor, row.billingCurrency)
              : typeof row.equalPerPayerShareMinor === "number"
                ? "Not a payer"
                : "-";
        const shareSecondary =
          row.isCurrentPayer && typeof row.currentUserTotalMinor === "number"
            ? `Current amount due incl. tax / ${formatCycleUnit(row.billingInterval)}`
            : row.isNextCyclePayer && typeof row.currentUserShareMinor === "number"
              ? `Queued share before tax / ${formatCycleUnit(row.billingInterval)}`
              : typeof row.equalPerPayerShareMinor === "number"
                ? `Equal payer share ${formatMoneyFromMinor(
                    row.equalPerPayerShareMinor,
                    row.billingCurrency
                  )} before tax`
                : "No payer amount published";
        const shareTertiary =
          typeof row.currentUserPendingChargesMinor === "number" &&
          row.currentUserPendingChargesMinor > 0
            ? `Pending charges ${formatMoneyFromMinor(
                row.currentUserPendingChargesMinor,
                row.billingCurrency
              )}`
            : null;
        const cardPrimary = row.paymentMethod
          ? `**** ${row.paymentMethod.last4 || "****"}`
          : row.isCurrentPayer || row.isNextCyclePayer
            ? "Needs card"
            : "No payer card";
        const cardSecondary = row.paymentMethod
          ? null
          : row.isCurrentPayer || row.isNextCyclePayer
            ? "Complete Team Payment Setup to save your card."
            : "Card details stay private to the selected payer.";
        const cardTertiary = row.currentUserBillingCountry
          ? `Billing country: ${row.currentUserBillingCountry}`
          : row.paymentMethodReady
            ? "Payment method ready"
            : null;
        const queuedChangeCopy = row.pendingNextCycleChange
          ? row.pendingNextCycleSummary
            ? isImmediatePendingChange(row)
              ? `${formatPlanLabel(
                  row.pendingNextCycleSummary.planKey
                )} pending immediate activation. Once approvals and cards are complete, only the prorated difference is billed before ${formatLocalBillingDeadline(
                  nextPaymentValue
                )}.`
              : `${formatPlanLabel(
                  row.pendingNextCycleSummary.planKey
                )} queued. Deadline ${formatLocalBillingDeadline(
                  row.pendingNextCycleSummary.deadlineAt
                )}.`
            : isImmediatePendingChange(row)
              ? "A billing upgrade is pending immediate activation."
              : "A new payer or plan structure is queued for the next cycle."
          : null;
        const subscriptionEndCopy = row.cancelAtPeriodEnd
          ? formatLocalBillingDeadline(row.currentPeriodEnd)
          : null;
        const subscriptionBoundaryLabel = isImmediatePendingChange(row)
          ? "Current renewal date"
          : row.pendingNextCycleChange
            ? "Current cycle end"
            : "Subscription end date";

        return {
          row,
          roleCopy,
          billableSeatCount: teamSetupMeta[row.teamId]?.billableSeatCount ?? null,
          teamCycleSubtotalMinor,
          grossTeamCycleMinor,
          discountSummary,
          nextPaymentPrimary,
          nextPaymentLabel,
          sharePrimary,
          shareSecondary,
          shareTertiary,
          cardPrimary,
          cardSecondary,
          cardTertiary,
          queuedChangeCopy,
          subscriptionEndCopy,
          subscriptionBoundaryLabel,
        };
      }),
    [billingTeams, teamSetupMeta]
  );

  const billingHistoryRows = useMemo(
    () =>
      (Array.isArray(billingHistory) ? billingHistory : []).map((entry) => {
        const statementMonthKey = getHistoryStatementMonthKey(entry);

        return {
          ...entry,
          statementMonthKey,
          statementMonthLabel: formatHistoryMonthLabel(statementMonthKey),
          statementTimestamp: getHistoryStatementMillis(entry),
          statusLabel: formatStatusLabel(entry?.status),
          sourceLabel: formatHistorySourceLabel(entry?.source),
          cycleWindowLabel: formatHistoryWindow(
            entry?.currentPeriodStart,
            entry?.currentPeriodEnd
          ),
          recordedAtLabel: formatLocalBillingDeadline(entry?.createdAt || entry?.updatedAt),
          teamTotalLabel: formatMoneyFromMinor(
            entry?.teamComputedTotalMinor,
            entry?.billingCurrency
          ),
          yourShareLabel: formatMoneyFromMinor(
            entry?.currentUserEntry?.finalInvoiceAmountMinor ??
              entry?.currentUserEntry?.recurringAmountMinor ??
              null,
            entry?.billingCurrency
          ),
          payersLabel: formatPayerNames(entry?.selectedPayerProfiles),
          latestInvoiceLabel:
            Array.isArray(entry?.latestInvoiceIds) && entry.latestInvoiceIds.length
              ? entry.latestInvoiceIds.join(", ")
              : "No invoice recorded",
        };
      }),
    [billingHistory]
  );

  const historyMonthOptions = useMemo(() => {
    const monthMap = new Map();

    billingHistoryRows.forEach((entry) => {
      if (!monthMap.has(entry.statementMonthKey)) {
        monthMap.set(entry.statementMonthKey, {
          value: entry.statementMonthKey,
          label: entry.statementMonthLabel,
        });
      }
    });

    return Array.from(monthMap.values()).sort((left, right) => {
      if (left.value === "undated") return 1;
      if (right.value === "undated") return -1;
      return right.value.localeCompare(left.value);
    });
  }, [billingHistoryRows]);

  const activeHistoryMonth =
    selectedHistoryMonth &&
    historyMonthOptions.some((option) => option.value === selectedHistoryMonth)
      ? selectedHistoryMonth
      : historyMonthOptions[0]?.value || "";

  const visibleBillingHistoryRows = useMemo(() => {
    if (!activeHistoryMonth) {
      return billingHistoryRows;
    }

    return billingHistoryRows.filter(
      (entry) => entry.statementMonthKey === activeHistoryMonth
    );
  }, [activeHistoryMonth, billingHistoryRows]);

  const runConfirmAction = useCallback(async () => {
    if (!confirmAction?.teamId) return;

    try {
      if (confirmAction.type === "cancel-renewal") {
        const cancelFn = httpsCallable(
          functions,
          FUNCTION_NAMES.cancelMyTeamSubscriptionRenewal
        );
        const result = await cancelFn({ teamId: confirmAction.teamId });
        const teamName = result?.data?.teamName || confirmAction.teamName;

        showAlert(
          `Renewal canceled for ${teamName}. Current access continues until the current billing period ends.`,
          "success"
        );
      } else if (confirmAction.type === "disconnect-card") {
        const disconnectFn = httpsCallable(
          functions,
          FUNCTION_NAMES.disconnectMyPayerCard
        );
        const result = await disconnectFn({ teamId: confirmAction.teamId });
        const teamName = result?.data?.teamName || confirmAction.teamName;

        showAlert(
          `Card disconnected for ${teamName}. Team renewal is now canceled for the next billing cycle, and any already-accrued charges remain due.`,
          "success"
        );
      }

      setConfirmAction(null);
      await loadBillingData();
      if (historyVisible) {
        await loadBillingHistory();
      }
      window.dispatchEvent(new Event("payer-action-overlay:refresh"));
    } catch (error) {
      console.error("runConfirmAction error:", error);
      showAlert(error?.message || "Billing action failed.", "error");
      throw error;
    }
  }, [confirmAction, historyVisible, loadBillingData, loadBillingHistory, showAlert]);

  const confirmMessage = useMemo(() => {
    if (!confirmAction) return "";

    if (confirmAction.type === "cancel-renewal") {
      return (
        `Cancel subscription renewal for team "${confirmAction.teamName}"?\n\n` +
        `This action affects only this team. It does not cancel subscriptions for any other teams on your account.\n\n` +
        `If you continue, all current payer subscriptions for this team will be set to end at the close of the current billing period.\n\n` +
        `Team access end date and time: ${confirmAction.currentPeriodEndLabel}\n\n` +
        `The team keeps access until that date and time, but it will not renew into the next billing cycle.\n\n` +
        `Any already-accrued or pending charges that were validly incurred before the period ends remain due.`
      );
    }

    return (
      `Disconnect your saved card for team "${confirmAction.teamName}"?\n\n` +
      `This action affects only this team. It does not disconnect cards or cancel subscriptions for any other teams on your account.\n\n` +
      `If you continue, your saved billing card for this team will be removed and team renewal will be canceled at the next billing date.\n\n` +
      `Team access end date and time: ${confirmAction.currentPeriodEndLabel}\n\n` +
      `Current access continues until that date and time.\n\n` +
      `Any already-accrued or pending charges remain due. If this team is on Pro, overage access stays restricted until a valid payment method is added back.`
    );
  }, [confirmAction]);

  const toggleExpandedTeamRow = useCallback((teamId) => {
    setExpandedTeamRows((current) => ({
      ...current,
      [teamId]: !current[teamId],
    }));
  }, []);

  const billingRootClassName = embedded
    ? "desktop-billing-page desktop-billing-page--embedded"
    : "desktop-billing-page";

  const confirmDialog = confirmAction ? (
    <DeleteHighlightConfirm
      message={confirmMessage}
      onCancel={() => setConfirmAction(null)}
      onConfirm={runConfirmAction}
      confirmLabel={
        confirmAction.type === "disconnect-card" ? "Disconnect Card" : "Cancel Renewal"
      }
      workingLabel={
        confirmAction.type === "disconnect-card" ? "Disconnecting..." : "Canceling..."
      }
      workingStatusLabel={
        confirmAction.type === "disconnect-card"
          ? "Disconnecting card..."
          : "Canceling renewal..."
      }
    />
  ) : null;

  const billingContent = (
    <div className={billingRootClassName}>
      <div className="desktop-section__header desktop-billing-page__header">
        <div>
          <p className="desktop-panel-eyebrow">Billing</p>
          <h2>Billing center</h2>
          <p>
            Review your payer responsibilities, current card visibility, renewal timing,
            and direct paths into each team&apos;s subscription or payment setup.
          </p>
        </div>

        <div className="desktop-billing-page__header-actions">
          <div className="desktop-billing-page__route-nav" aria-label="Billing navigation">
            <button
              type="button"
              className={`desktop-secondary-button desktop-billing-page__route-button ${
                historyVisible ? "" : "is-active"
              }`}
              onClick={() => setHistoryVisible(false)}
            >
              Billing center
            </button>
            <button
              type="button"
              className={`desktop-secondary-button desktop-billing-page__route-button ${
                historyVisible ? "is-active" : ""
              }`}
              onClick={() => {
                setHistoryVisible(true);
                if (!billingHistory.length && !historyLoading) {
                  void loadBillingHistory();
                }
              }}
            >
              Billing History
            </button>
            <button
              type="button"
              className="desktop-secondary-button desktop-billing-page__route-button"
              onClick={() =>
                activeTeamId
                  ? navigate(buildTeamScopedPath("/team-setup-billing", activeTeamId))
                  : undefined
              }
              disabled={!activeTeamId}
            >
              Team Subscription
            </button>
            <button
              type="button"
              className="desktop-secondary-button desktop-billing-page__route-button"
              onClick={() =>
                activeTeamId
                  ? navigate(buildTeamScopedPath("/team-setup-payers", activeTeamId))
                  : undefined
              }
              disabled={!activeTeamId}
            >
              Team Payment Setup
            </button>
          </div>

          {!embedded ? (
            <button
              type="button"
              className="desktop-secondary-button desktop-billing-page__route-button"
              onClick={() => navigate("/account#billing")}
            >
              Back to account
            </button>
          ) : null}

          <button
            type="button"
            className="desktop-secondary-button desktop-billing-page__refresh-button"
            onClick={() => {
              if (historyVisible) {
                void loadBillingHistory();
                return;
              }

              void loadBillingData();
            }}
            aria-label="Refresh billing center"
            title="Refresh billing center"
          >
            Refresh
          </button>
        </div>
      </div>

      {pageError ? (
        <div className="desktop-banner desktop-banner--error">
          <strong>Billing failed to load</strong>
          <span>{pageError}</span>
        </div>
      ) : null}

      {!historyVisible ? (
        <div
          className={`desktop-billing-page__status-strip desktop-billing-page__status-strip--${billingStatusStrip.tone}`}
        >
          <div className="desktop-billing-page__status-copy">
            <strong>{billingStatusStrip.title}</strong>
            <span>{billingStatusStrip.copy}</span>
          </div>

          {billingStatusStrip.actionTeamId ? (
            <button
              type="button"
              className="desktop-secondary-button desktop-secondary-button--compact"
              onClick={() =>
                navigate(
                  buildTeamScopedPath("/team-setup-payers", billingStatusStrip.actionTeamId)
                )
              }
            >
              Resolve now
            </button>
          ) : null}
        </div>
      ) : null}

      {historyVisible ? (
        <section className="desktop-review-card desktop-review-card--subpanel desktop-billing-page__history-card">
          <div className="desktop-review-card__top">
            <div>
              <h3>Billing history</h3>
              <p>
                Monthly statement-style billing records for the teams currently attached to this
                account.
              </p>
            </div>

            <div className="desktop-billing-page__history-actions">
              <label className="desktop-billing-page__history-filter">
                <span>Statement month</span>
                <select
                  className="desktop-billing-page__history-select"
                  value={activeHistoryMonth}
                  onChange={(event) => setSelectedHistoryMonth(event.target.value)}
                  disabled={!historyMonthOptions.length || historyLoading}
                >
                  {historyMonthOptions.length ? (
                    historyMonthOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))
                  ) : (
                    <option value="">No statements yet</option>
                  )}
                </select>
              </label>

              <button
                type="button"
                className="desktop-secondary-button desktop-secondary-button--compact"
                onClick={() => void loadBillingHistory()}
              >
                Refresh history
              </button>
            </div>
          </div>

          {historyError ? (
            <div className="desktop-banner desktop-banner--error">
              <strong>Billing history failed to load</strong>
              <span>Server response: {historyError}</span>
            </div>
          ) : null}

          {historyLoading ? (
            <div className="desktop-empty-copy">Loading billing history...</div>
          ) : historyError ? null : visibleBillingHistoryRows.length ? (
            <div className="desktop-billing-page__history-list">
              {visibleBillingHistoryRows.map((entry) => (
                <article
                  key={`${entry.teamId}-${entry.historyId}`}
                  className="desktop-billing-page__history-entry"
                >
                  <div className="desktop-billing-page__history-header">
                    <div className="desktop-billing-page__history-heading">
                      <strong>{entry.teamName}</strong>
                      <span className="desktop-billing-page__history-subline">
                        {formatPlanLabel(entry.planKey)} plan
                      </span>
                    </div>

                    <div className="desktop-billing-page__history-pills">
                      <span className="desktop-chip">{entry.sourceLabel}</span>
                      <span className="desktop-pill desktop-pill--muted">{entry.statusLabel}</span>
                    </div>
                  </div>

                  <div className="desktop-billing-page__history-grid">
                    <div className="desktop-billing-page__history-metric">
                      <span>Cycle window</span>
                      <strong>{entry.cycleWindowLabel}</strong>
                    </div>
                    <div className="desktop-billing-page__history-metric">
                      <span>Recorded at</span>
                      <strong>{entry.recordedAtLabel}</strong>
                    </div>
                    <div className="desktop-billing-page__history-metric">
                      <span>Team total</span>
                      <strong>{entry.teamTotalLabel}</strong>
                    </div>
                    <div className="desktop-billing-page__history-metric">
                      <span>Your share</span>
                      <strong>{entry.yourShareLabel}</strong>
                    </div>
                  </div>

                  <div className="desktop-billing-page__history-detail-list">
                    <div className="desktop-billing-page__history-detail-row">
                      <span>Payers</span>
                      <strong>{entry.payersLabel}</strong>
                    </div>
                    <div className="desktop-billing-page__history-detail-row">
                      <span>Invoices</span>
                      <strong>{entry.latestInvoiceLabel}</strong>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="desktop-empty-copy">
              {historyMonthOptions.length
                ? "No billing records are available for the selected statement month."
                : "No recorded billing cycles are available for this account yet."}
            </div>
          )}
        </section>
      ) : null}

      {!historyVisible ? (
        <div className="desktop-billing-page__matrix">
        <section className="desktop-review-card desktop-review-card--subpanel desktop-billing-page__breakdown-card">
          <div className="desktop-review-card__top desktop-billing-page__breakdown-top">
            <div>
              <h3>Billing breakdown</h3>
              <p>
                Team-by-team view of the subscription cost, your share, card status,
                next payment timing, and direct action buttons.
              </p>
            </div>

            <div className="desktop-billing-page__breakdown-metrics">
              <div className="desktop-billing-page__mini-stat">
                <span>Teams billed</span>
                <strong>{billingTeams.length}</strong>
              </div>
              <div className="desktop-billing-page__mini-stat">
                <span>Your payer roles</span>
                <strong>{currentPayerRoleCount}</strong>
              </div>
              <div className="desktop-billing-page__mini-stat">
                <span>Total due</span>
                <strong>
                  {formatMoneyFromMinor(
                    billingOverview?.totalMonthlyCostMinor,
                    billingOverview?.currency
                  )}
                </strong>
              </div>
            </div>
          </div>

          <div className="desktop-billing-page__table-head" aria-hidden="true">
            <span>Team names</span>
            <span>Your share</span>
            <span>Card</span>
            <span>Next payment date</span>
            <span>Action buttons</span>
          </div>

          <div className="desktop-billing-page__breakdown-list">
            {billingBreakdownRows.map(
              ({
                row,
                roleCopy,
                billableSeatCount,
                teamCycleSubtotalMinor,
                grossTeamCycleMinor,
                discountSummary,
                nextPaymentPrimary,
                nextPaymentLabel,
                sharePrimary,
                shareSecondary,
                shareTertiary,
                cardPrimary,
                cardSecondary,
                cardTertiary,
                queuedChangeCopy,
                subscriptionEndCopy,
                subscriptionBoundaryLabel,
              }) => {
                const isExpanded =
                  expandedTeamRows[row.teamId] ?? row.teamId === activeTeamId;
                const teamDetailsId = `billing-team-${row.teamId}`;

                return (
                  <article
                    key={row.teamId}
                    className={`desktop-billing-page__breakdown-row ${
                      row.teamId === activeTeamId ? "is-active-team" : ""
                    } ${isExpanded ? "is-expanded" : "is-collapsed"}`}
                  >
                    <div className="desktop-billing-page__breakdown-team">
                      <div className="desktop-billing-page__team-heading">
                        <strong>{row.teamName}</strong>
                        <span className={`desktop-pill desktop-pill--${getStatusTone(row)}`}>
                          {getStatusBadgeLabel(row)}
                        </span>
                        {row.teamId === activeTeamId ? (
                          <span className="desktop-chip">Active team</span>
                        ) : null}
                      </div>

                      <div className="desktop-billing-page__team-subline">
                        <span className="desktop-billing-page__team-subline-text">
                          {formatPlanLabel(row.planKey)} plan | {roleCopy}
                        </span>
                        <button
                          type="button"
                          className="desktop-secondary-button desktop-secondary-button--compact desktop-secondary-button--icon desktop-billing-page__expand-button"
                          aria-expanded={isExpanded}
                          aria-controls={teamDetailsId}
                          aria-label={
                            isExpanded
                              ? `Collapse ${row.teamName} billing details`
                              : `Expand ${row.teamName} billing details`
                          }
                          title={isExpanded ? "Hide details" : "Expand details"}
                          onClick={() => toggleExpandedTeamRow(row.teamId)}
                        >
                          {isExpanded ? "-" : "+"}
                        </button>
                      </div>

                      <div
                        id={teamDetailsId}
                        className={`desktop-billing-page__team-details ${
                          isExpanded ? "is-expanded" : "is-collapsed"
                        }`}
                      >
                        <div className="desktop-billing-page__team-detail">
                          <span>Billable seats on team</span>
                          <strong>
                            {typeof billableSeatCount === "number" ? billableSeatCount : "-"}
                          </strong>
                        </div>
                        <div className="desktop-billing-page__team-detail">
                          <span>Payees selected</span>
                          <strong>{row.selectedPayerCount || 0}</strong>
                        </div>
                        <div className="desktop-billing-page__team-detail">
                          <span>Total subscription / cycle</span>
                          <strong>
                            {formatMoneyFromMinor(grossTeamCycleMinor, row.billingCurrency)}
                          </strong>
                        </div>
                        <div className="desktop-billing-page__team-detail">
                          <span>Discounts</span>
                          <strong>{discountSummary.primary}</strong>
                        </div>
                        <div className="desktop-billing-page__team-detail">
                          <span>Discount value</span>
                          <strong>{discountSummary.secondary || "-"}</strong>
                        </div>
                        <div className="desktop-billing-page__team-detail">
                          <span>Net after discounts</span>
                          <strong>
                            {formatMoneyFromMinor(teamCycleSubtotalMinor, row.billingCurrency)}
                          </strong>
                        </div>
                        <div className="desktop-billing-page__team-detail desktop-billing-page__team-detail--full">
                          <span>Payers</span>
                          <strong>{formatPayerNames(row.selectedPayerProfiles)}</strong>
                        </div>
                        {subscriptionEndCopy ? (
                          <div className="desktop-billing-page__team-detail desktop-billing-page__team-detail--full">
                            <span>{subscriptionBoundaryLabel}</span>
                            <strong>{subscriptionEndCopy}</strong>
                          </div>
                        ) : null}
                        {queuedChangeCopy ? (
                          <div className="desktop-billing-page__team-detail desktop-billing-page__team-detail--full">
                            <span>Next billing cycle change planned</span>
                            <strong>{queuedChangeCopy}</strong>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="desktop-billing-page__table-cell">
                      <span className="desktop-billing-page__cell-label">Your share</span>
                      <strong>{sharePrimary}</strong>
                      <span className="desktop-billing-page__cell-copy">{shareSecondary}</span>
                      {shareTertiary ? (
                        <span className="desktop-billing-page__cell-copy desktop-billing-page__cell-copy--warning">
                          {shareTertiary}
                        </span>
                      ) : null}
                    </div>

                    <div className="desktop-billing-page__table-cell">
                      <span className="desktop-billing-page__cell-label">Card</span>
                      <strong>{cardPrimary}</strong>
                      {cardSecondary ? (
                        <span className="desktop-billing-page__cell-copy">{cardSecondary}</span>
                      ) : null}
                      {cardTertiary ? (
                        <span className="desktop-billing-page__cell-copy">{cardTertiary}</span>
                      ) : null}
                    </div>

                    <div className="desktop-billing-page__table-cell">
                      <span className="desktop-billing-page__cell-label">Next payment date</span>
                      <strong>{nextPaymentPrimary}</strong>
                      <span className="desktop-billing-page__cell-copy">{nextPaymentLabel}</span>
                      <span className="desktop-billing-page__cell-copy">
                        {getPrimaryActionCopy(row)}
                      </span>
                    </div>

                    <div className="desktop-billing-page__table-cell desktop-billing-page__table-cell--actions">
                      <span className="desktop-billing-page__cell-label">Action buttons</span>
                      <div className="desktop-inline-actions desktop-inline-actions--wrap desktop-billing-page__row-actions">
                        <button
                          type="button"
                          className="desktop-secondary-button desktop-secondary-button--compact"
                          onClick={() =>
                            navigate(buildTeamScopedPath("/team-setup-billing", row.teamId))
                          }
                        >
                          Team Subscription
                        </button>
                        <button
                          type="button"
                          className="desktop-secondary-button desktop-secondary-button--compact"
                          onClick={() =>
                            navigate(buildTeamScopedPath("/team-setup-payers", row.teamId))
                          }
                        >
                          Team Payment Setup
                        </button>
                      </div>
                    </div>
                  </article>
                );
              }
            )}

            {!billingBreakdownRows.length && !loading ? (
              <div className="desktop-empty-copy">
                No team billing rows are attached to this account yet.
              </div>
            ) : null}
          </div>

          <div className="desktop-billing-page__totals">
            <div className="desktop-billing-page__total-row">
              <span>Total monthly due amount pre tax</span>
              <strong>
                {formatMoneyFromMinor(
                  billingOverview?.totalMonthlySubtotalMinor,
                  billingOverview?.currency
                )}
              </strong>
            </div>
            <div className="desktop-billing-page__total-row">
              <span>Tax</span>
              <strong>
                {formatMoneyFromMinor(
                  billingOverview?.totalMonthlyTaxMinor,
                  billingOverview?.currency
                )}
              </strong>
            </div>
            <div className="desktop-billing-page__total-row desktop-billing-page__total-row--grand">
              <span>Total monthly due amount by you</span>
              <strong>
                {formatMoneyFromMinor(
                  billingOverview?.totalMonthlyCostMinor,
                  billingOverview?.currency
                )}
              </strong>
            </div>
          </div>
        </section>

        <aside className="desktop-billing-page__rail">
          <section className="desktop-review-card desktop-review-card--subpanel">
            <div className="desktop-review-card__top">
              <div>
                <h3>How billing works</h3>
                <p>
                  Keep the billing rules visible without pushing the actual charges
                  off the page.
                </p>
              </div>
            </div>

            <div className="desktop-billing-page__rule-list">
              <div className="desktop-billing-page__rule-row">
                <strong>Approvals</strong>
                <span>
                  Each selected payer approves only their own share and adds only
                  their own card.
                </span>
              </div>
              <div className="desktop-billing-page__rule-row">
                <strong>Queued changes</strong>
                <span>
                  Upgrades or extra storage can activate immediately with proration
                  once approvals and cards are ready. Downgrades, cancellations,
                  and payer-structure changes wait for the next cycle.
                </span>
              </div>
              <div className="desktop-billing-page__rule-row">
                <strong>Card removal</strong>
                <span>
                  Disconnecting a payer card stops that team&apos;s next renewal,
                  but already-accrued charges still remain due.
                </span>
              </div>
              <div className="desktop-billing-page__rule-row">
                <strong>Renewals ending</strong>
                <span>
                  {renewalEndingCount} team billing cycle
                  {renewalEndingCount === 1 ? "" : "s"} currently have renewal
                  turned off.
                </span>
              </div>
            </div>
          </section>

          <section className="desktop-review-card desktop-review-card--subpanel">
            <div className="desktop-review-card__top">
              <div>
                <h3>Your payment method</h3>
                <p>
                  Manage the saved card this billing view can currently see, plus
                  the country and status attached to it.
                </p>
              </div>
            </div>

            {primaryPaymentRow ? (
              <div className="desktop-billing-page__card-panel">
                <div className="desktop-billing-page__card-chip">
                  {primaryPaymentRow.paymentMethod ? (
                    <>
                      <strong>
                        {primaryPaymentRow.paymentMethod.brand
                          ? primaryPaymentRow.paymentMethod.brand.toUpperCase()
                          : "CARD"}
                      </strong>
                      <span>
                        Ending in {primaryPaymentRow.paymentMethod.last4 || "****"}
                      </span>
                      <span>
                        Expires{" "}
                        {primaryPaymentRow.paymentMethod.expMonth
                          ? String(primaryPaymentRow.paymentMethod.expMonth).padStart(2, "0")
                          : "??"}
                        /{primaryPaymentRow.paymentMethod.expYear || "????"}
                      </span>
                    </>
                  ) : (
                    <>
                      <strong>No saved card</strong>
                      <span>Add a card in Team Payment Setup.</span>
                    </>
                  )}
                </div>

                <div className="desktop-billing-page__card-copy">
                  {primaryPaymentRow.paymentMethod
                    ? `This card is currently attached to your payer record for ${primaryPaymentRow.teamName}.`
                    : `No saved card is currently attached to your payer record for ${primaryPaymentRow.teamName}.`}
                </div>

                <div className="desktop-billing-page__detail-list">
                  <div className="desktop-billing-page__detail-row">
                    <span>Card display</span>
                    <strong>
                      {formatPaymentMethodSummary(primaryPaymentRow.paymentMethod)}
                    </strong>
                  </div>
                  <div className="desktop-billing-page__detail-row">
                    <span>Billing country</span>
                    <strong>
                      {primaryPaymentRow.currentUserBillingCountry || "Not saved yet"}
                    </strong>
                  </div>
                  <div className="desktop-billing-page__detail-row">
                    <span>Status</span>
                    <strong>
                      {primaryPaymentRow.paymentMethod
                        ? "Connected"
                        : primaryPaymentRow.isCurrentPayer || primaryPaymentRow.isNextCyclePayer
                          ? "Card still needed"
                          : "Not one of the payers"}
                    </strong>
                  </div>
                  <div className="desktop-billing-page__detail-row">
                    <span>Visible on team</span>
                    <strong>{primaryPaymentRow.teamName}</strong>
                  </div>
                  <div className="desktop-billing-page__detail-row">
                    <span>Saved cards on billing view</span>
                    <strong>{savedCardCount}</strong>
                  </div>
                </div>

                <div className="desktop-inline-actions desktop-inline-actions--wrap desktop-billing-page__card-actions">
                  <button
                    type="button"
                    className="desktop-secondary-button desktop-secondary-button--compact"
                    onClick={() =>
                      navigate(buildTeamScopedPath("/team-setup-payers", primaryPaymentRow.teamId))
                    }
                  >
                    {primaryPaymentRow.paymentMethod ? "Manage card" : "Add card"}
                  </button>
                  <button
                    type="button"
                    className="desktop-secondary-button desktop-secondary-button--compact"
                    disabled={
                      !primaryPaymentRow.paymentMethod ||
                      !(primaryPaymentRow.isCurrentPayer || primaryPaymentRow.isNextCyclePayer)
                    }
                    onClick={() =>
                      setConfirmAction({
                        type: "disconnect-card",
                        teamId: primaryPaymentRow.teamId,
                        teamName: primaryPaymentRow.teamName,
                        currentPeriodEndLabel: formatLocalBillingDeadline(
                          primaryPaymentRow.currentPeriodEnd
                        ),
                      })
                    }
                  >
                    Disconnect card
                  </button>
                </div>

                {primaryPaymentRow.billingPolicy?.overageRestrictedWithoutValidPaymentMethod &&
                primaryPaymentRow.overageAccessRestricted ? (
                  <div className="desktop-billing-page__card-copy desktop-billing-page__card-copy--warning">
                    Overage access is currently restricted until a valid payment
                    method is added back.
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="desktop-empty-copy">
                No payer card is attached to this billing view yet.
              </div>
            )}
          </section>
        </aside>
      </div>
      ) : null}
    </div>
  );

  if (embedded) {
    return (
      <>
        {toast.message ? (
          <CustomAlert
            message={toast.message}
            type={toast.type}
            position="top"
            onClose={() => setToast({ message: "", type: "" })}
          />
        ) : null}
        {billingContent}
        {confirmDialog}
      </>
    );
  }

  return (
    <div className="desktop-page">
      {toast.message ? (
        <CustomAlert
          message={toast.message}
          type={toast.type}
          position="top"
          onClose={() => setToast({ message: "", type: "" })}
        />
      ) : null}

      <section className="desktop-section">
        {billingContent}
        {confirmDialog}
      </section>

      <Footer />
    </div>
  );
};

export default Billing;
